import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { ListingID, SessionService } from "@recore-solutions/nstar-lib";
import { combineLatest, defer, EMPTY, Observable, of, timer } from "rxjs";
import { debounce, map, switchMap } from "rxjs/operators";
import { ConfigurationService } from "./configuration.service";
import { selectListingPermissionsByListingId } from "./listing-permission.selectors";
import { selectListingSummaryByListingId } from "./listing-summary.selectors";
import { Configuration } from "./shared/interfaces/configuration";
import { ListingPermission, PermissionToken } from "./shared/interfaces/listing-permission";
import { ListingSummaryService } from "./shared/services/listing-summary.service";
import { switchMapToIfUndefined } from "./shared/switchMapToIfUndefined";

@Injectable({
	providedIn: "root",
})
export class PermissionListGuard {
	constructor(
		private router: Router,
		private sessionService: SessionService,
		private configService: ConfigurationService,
		private listingSummaryService: ListingSummaryService
	) {}

	canActivate(
		next: ActivatedRouteSnapshot,
		_state: RouterStateSnapshot
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		return this.hasPermissionLevelsAndList(
			next.root.firstChild?.params?.mlid || next.root.firstChild?.firstChild?.params?.mlid,
			next.routeConfig.data.permissionLevels,
			next.routeConfig.data.permissionList,
			next.routeConfig.data.excludePermissionLevels,
			[],
			next?.root?.firstChild?.data?.removeAlertName === "Polaris"
		).pipe(map((bool) => bool || this.router.createUrlTree(["/"])));
	}
	canActivateChild(
		next: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
		return this.canActivate(next, state);
	}

	hasPermissionLevelsAndList(
		listingId: ListingID,
		permissionLevels?: string[] | ((config: Configuration) => string[]),
		permissionList?: string[],
		excludePermissionLevels?: string[],
		_excludePermissionList?: string[],
		_legacy = false
	): Observable<boolean> {
		const obs: Observable<boolean>[] = [of(true)];
		if (permissionLevels) {
			if (!Array.isArray(permissionLevels)) {
				permissionLevels = permissionLevels(this.configService.config);
			}
			obs.push(
				this.sessionService.permissionLevel$.pipe(
					map(
						(level) =>
							!!(permissionLevels as string[]).find((p) =>
								level.find((l) => l.accessName.toLowerCase() === p.toLowerCase())
							)
					)
				)
			);
		}
		if (permissionList) {
			if (!listingId) {
				return of(false);
			}
			obs.push(this.listingHasAllPermissionTokens(listingId, permissionList as PermissionToken[]));
		}
		if (!!excludePermissionLevels) {
			obs.push(
				this.sessionService.permissionLevel$.pipe(
					map(
						(level) =>
							!level.every((l) =>
								excludePermissionLevels.some((p) => l.accessName.toLowerCase() === p.toLowerCase())
							)
					)
				)
			);
		}
		return combineLatest(obs).pipe(map((bools) => bools.every((bool) => bool)));
	}

	listingHasPermissionToken(
		listingId: ListingID,
		permissionToken: PermissionToken
	): Observable<boolean> {
		return this.listingHasAllPermissionTokens(listingId, [permissionToken]);
	}

	buildPermissionTokensObject = (
		permissionTokens: PermissionToken[],
		granted: boolean = false
	): { [permissionToken: string]: boolean } => {
		const obj: { [permissionToken: string]: boolean } = {};
		permissionTokens.forEach((t) => (obj[t] = granted));
		return obj;
	};

	getListingPermission$ = (listingId: ListingID): Observable<ListingPermission[]> => {
		if (!listingId) {
			return of([]);
		}
		// If the listing summary is in the store already, then we don't need to request it to get permissions.
		const listingSummaryInStore$ = this.listingSummaryService.store.select(
			selectListingSummaryByListingId(listingId)
		);
		// If it isn't, make a request and use the result from the cloud. Use defer so we don't send the request unless/until we need.
		const listingSummaryInCloud$ = defer(() => this.listingSummaryService.getByKey(listingId));
		// Defer this as well, so that we only select from the store after the permissions have been loaded via the summary request.
		const listingPermission$ = defer(() =>
			this.listingSummaryService.store.select(selectListingPermissionsByListingId(listingId))
		);

		return listingSummaryInStore$.pipe(
			debounce((listingSummary) => (!listingSummary ? timer(0) : of(EMPTY))), // If we don't have a ListingSummary, debounce for a cycle in case a request is finishing.
			switchMapToIfUndefined(listingSummaryInCloud$), // If we still don't have a ListingSummary, switch to a request from the cloud.
			switchMap(() => listingPermission$) // Now that we have a ListingSummary, we also have permissions.
		);
	};

	listingPermissionTokensObject = (
		listingId: ListingID
	): Observable<{ [permissionToken: string]: boolean }> => {
		if (!listingId) {
			return of(this.buildPermissionTokensObject([]));
		}
		return this.getListingPermission$(listingId).pipe(
			map(this.mapPermissionTokensToPermissionTokensObject)
		);
	};

	mapPermissionTokensToPermissionTokensObject = (
		permissions: ListingPermission[]
	): { [permissionToken: string]: boolean } => {
		return this.buildPermissionTokensObject(
			permissions.map((p) => p.permission),
			true
		);
	};

	listingHasAllPermissionTokens(
		listingId: ListingID,
		permissionTokens: PermissionToken[]
	): Observable<boolean> {
		if (!listingId) {
			return of(false);
		}
		return this.getListingPermission$(listingId).pipe(
			map(this.allPermissionTokensMatchAtLeastOneListingPermission(permissionTokens)) // Return true if all PermissionTokens match at least one ListingPermission
		);
	}

	listingPermissionMatchesPermissionToken = (permissionToken: PermissionToken) => {
		return (listingPermission: ListingPermission): boolean =>
			listingPermission.permission === permissionToken;
	};

	permissionTokenMatchesOneListingPermission = (listingPermissions: ListingPermission[]) => {
		return (permissionTokens: PermissionToken): boolean =>
			!!listingPermissions.find(this.listingPermissionMatchesPermissionToken(permissionTokens));
	};

	allPermissionTokensMatchAtLeastOneListingPermission = (permissionTokens: PermissionToken[]) => {
		return (listingPermissions: ListingPermission[]) =>
			(permissionTokens || []).every(
				this.permissionTokenMatchesOneListingPermission(listingPermissions)
			);
	};
}
